import { CHAT_HIDDEN_ROUTES, SUPPORTED_CHAT_REGION_IDS, CHAT_DISPLAY_DELAY_MS } from "../constants/appConstants";
import { hideWidget, showWidget } from "../services/agentCoreChatService";

/**
 * Determines whether the AgentCore chat widget is supported for a given region.
 * The chat widget is currently only available for US (regionId 840) and Canada (regionId 124).
 *
 * @param {string} regionId - The region ID to check.
 * @returns {boolean} True if chat is supported for this region, false otherwise.
 *
 * @example
 * isChatSupportedForRegion("840"); // true (US)
 * isChatSupportedForRegion("124"); // true (Canada)
 * isChatSupportedForRegion("276"); // false (EU - Germany)
 */
export const isChatSupportedForRegion = (regionId) => {
  return SUPPORTED_CHAT_REGION_IDS.includes(regionId);
};

/**
 * Determines whether the chat widget should be hidden for a given route path.
 *
 * @param {string} path - The route path to check (e.g., "/logout", "/account/technicianAppointments").
 * @returns {boolean} True if the chat should be hidden for this route, false otherwise.
 *
 * @example
 * isChatHiddenForRoute("/logout"); // true
 * isChatHiddenForRoute("/account/technicianAppointments/device-transfer"); // true
 * isChatHiddenForRoute("/dashboard"); // false
 */
export const isChatHiddenForRoute = (path) => {
  const normalizedPath = path.toLowerCase();
  return CHAT_HIDDEN_ROUTES.some((route) => {
    const normalizedRoute = route.toLowerCase();
    // Match exact route or any sub-route (e.g., /logout/confirm)
    return normalizedPath === normalizedRoute || normalizedPath.startsWith(normalizedRoute + "/");
  });
};

export const chatDisplayHandler = (path) => {
  const shouldHide = isChatHiddenForRoute(path);

  if (shouldHide) {
    setTimeout(() => {
      showOrHideChatButton("none");
    }, CHAT_DISPLAY_DELAY_MS);
  } else {
    showOrHideChatButton("block");
  }
};

const showOrHideChatButton = (displayValue) => {
  if (displayValue === "none") {
    hideWidget();
  } else {
    showWidget();
  }
};

// eslint-disable-next-line no-unused-vars
/* export const chatScript = (firstName, surname, emailAddress, phoneNumber) => {
  if (
    window.location.href.toString().includes("vccp-uat.ebiz.verizon.com") ||
    window.location.href.toString().includes("reveal.stage.us.fleetmatics.com") ||
    window.location.href.toString().includes("reveal.stage.us.vzconnect.com") ||
    window.location.href.toString().includes("vccp-qa.ebiz.verizon.com") ||
    window.location.href.toString().includes("vccp-dev.ebiz.verizon.com") ||
    window.location.href.toString().includes("localhost")
  ) {
    //Non-Prod
    window.icPatronChat.init({
      serverHost: "https://home-c31.incontact.com",
      bus_no: 4598553,
      poc: "2b366d46-4c6b-4043-ba17-d5aa57cfb6d2",
      params: [firstName, surname, emailAddress, phoneNumber],
    });
  } else {
    //Prod
    window.icPatronChat.init({
      serverHost: "https://home-c31.incontact.com",
      bus_no: 4598553,
      poc: "158347ad-78f6-45fa-b010-7414ca2cd24e",
      params: [firstName, surname, emailAddress, phoneNumber],
    });
  }
}; */

/**
 * Hides the AgentCore chat widget when a user logs out or their session expires.
 *
 * This function is called from logout/session-expired pages to ensure the chat
 * widget is hidden. App.js unmounts the widget on logout/session-expired routes;
 * this only collapses any already-mounted panel until that unmount runs.
 *
 * @param {string} regionId - The user's region ID (e.g., "840" for US, "124" for Canada).
 * @param {string} emailAddress - The user's email address (used to verify user is logged in).
 *
 * @example
 * // Called from logout page
 * hideChatOnLogout("840", "user@example.com");
 *
 * @see {@link chatDisplayHandler} for the actual hide/show implementation
 * @see {@link destroyWidget} in agentCoreChatService.js for full widget cleanup
 */
export const hideChatOnLogout = (regionId, emailAddress) => {
  if (isChatSupportedForRegion(regionId) && emailAddress) {
    // Always use a path that triggers hide — currentPath still reflects the
    // previous page at the time of session expiry / logout, so passing it
    // would show the chat again for any normal route.
    chatDisplayHandler("/sessionExpired");
  }
};

// Deprecated: Use hideChatOnLogout instead. Kept for backward compatibility.
export const removeChat = hideChatOnLogout;
